import _asyncToGenerator from "/home/hyuto/Hyuto.github.io/v3-beta/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _regeneratorRuntime from "/home/hyuto/Hyuto.github.io/v3-beta/node_modules/@babel/runtime/regenerator/index.js";

/**
 * @license
 * Copyright 2018 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import { convertToTensor } from '../tensor_util_env';
import * as util from '../util';
import { gather } from './gather';
import { reshape } from './reshape';
import { squeeze } from './squeeze';
import { whereAsync } from './where_async';
/**
 * Apply boolean mask to tensor.
 *
 * ```js
 * const tensor = tf.tensor2d([1, 2, 3, 4, 5, 6], [3, 2]);
 * const mask = tf.tensor1d([1, 0, 1], 'bool');
 * const result = await tf.booleanMaskAsync(tensor, mask);
 * result.print();
 * ```
 *
 * @param tensor N-D tensor.
 * @param mask K-D boolean tensor, K <= N and K must be known statically.
 * @param axis A 0-D int Tensor representing the axis in tensor to mask from.
 *     By default, axis is 0 which will mask from the first dimension.
 *     Otherwise K + axis <= N.
 *
 * @doc {heading: 'Tensors', subheading: 'Slicing and Joining'}
 */

function booleanMaskAsync_(_x, _x2, _x3) {
  return _booleanMaskAsync_.apply(this, arguments);
}

function _booleanMaskAsync_() {
  _booleanMaskAsync_ = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(tensor, mask, axis) {
    var $tensor, $mask, axisFrom, maskDim, tensorShape, leadingSize, i, targetTensorShape, reshapedTensor, reshapedMask, positivePositions, indices, res;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            $tensor = convertToTensor(tensor, 'tensor', 'boolMask');
            $mask = convertToTensor(mask, 'mask', 'boolMask', 'bool');
            axisFrom = axis == null ? 0 : axis;
            maskDim = $mask.rank;
            tensorShape = $tensor.shape;
            util.assert(maskDim > 0, function () {
              return 'mask cannot be scalar';
            });
            util.assertShapesMatch(tensorShape.slice(axisFrom, axisFrom + maskDim), $mask.shape, "mask's shape must match the first K dimensions of tensor's shape,");
            leadingSize = 1;

            for (i = axisFrom; i < axisFrom + maskDim; i++) {
              leadingSize *= tensorShape[i];
            }

            targetTensorShape = tensorShape.slice(0, axisFrom).concat([leadingSize], tensorShape.slice(axisFrom + maskDim));
            reshapedTensor = reshape($tensor, targetTensorShape);
            reshapedMask = reshape($mask, [-1]);
            _context.next = 14;
            return whereAsync(reshapedMask);

          case 14:
            positivePositions = _context.sent;
            indices = squeeze(positivePositions, [1]);
            res = gather(reshapedTensor, indices, axisFrom); // Ensure no memory leak.

            if (tensor !== $tensor) {
              $tensor.dispose();
            }

            if (mask !== $mask) {
              $mask.dispose();
            }

            indices.dispose();
            reshapedTensor.dispose();
            reshapedMask.dispose();
            positivePositions.dispose();
            return _context.abrupt("return", res);

          case 24:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));
  return _booleanMaskAsync_.apply(this, arguments);
}

export var booleanMaskAsync = booleanMaskAsync_;